import { create } from 'zustand';
import { api } from '../services/api';

export interface HashtagMedia {
  id: string;
  caption?: string;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  media_url?: string;
  permalink?: string;
  timestamp?: string;
  like_count?: number;
  comments_count?: number;
}

export interface HashtagMetrics {
  hashtag: string;
  hashtagId: string;
  topMediaCount: number;
  recentMediaCount: number;
  avgLikes: number;
  avgComments: number;
  avgEngagement: number;
  topPosts: HashtagMedia[];
  recentPosts: HashtagMedia[];
  platform: 'instagram';
  searchedAt: string;
}

export interface HashtagLimitStatus {
  searchesThisWeek: number;
  remaining: number;
  resetsIn: string;
  hashtags: string[];
}

export interface HashtagSuggestion {
  name: string;
  mediaCount?: number;
  cachedAt: number;
}

export interface HashtagAnalysisResult {
  hashtags: HashtagMetrics[];
  recommendations: string[];
  limitStatus: {
    remaining: number;
    warning?: string;
  };
}

interface HashtagApiStatus {
  configured: boolean;
  platforms: {
    instagram: boolean;
    facebook: boolean;
  };
  limits?: {
    instagram: {
      searchesThisWeek: number;
      remaining: number;
      maxPerWeek: number;
      resetsIn: string;
      recentSearches: string[];
    };
  };
  info?: {
    instagram: string;
    facebook: string;
  };
}

interface HashtagsState {
  status: HashtagApiStatus | null;
  limitStatus: HashtagLimitStatus | null;
  analysisResults: HashtagMetrics[];
  recommendations: string[];
  suggestions: HashtagSuggestion[];
  isLoading: boolean;
  error: string | null;

  fetchStatus: () => Promise<void>;
  fetchLimits: () => Promise<void>;
  getSuggestions: (query: string) => Promise<HashtagSuggestion[]>;
  searchHashtag: (hashtag: string) => Promise<{ id: string; name: string } | null>;
  getHashtagMetrics: (hashtag: string) => Promise<HashtagMetrics | null>;
  analyzeHashtags: (hashtags: string[]) => Promise<HashtagAnalysisResult | null>;
  clearResults: () => void;
  clearError: () => void;
}

export const useHashtagsStore = create<HashtagsState>((set, get) => ({
  status: null,
  limitStatus: null,
  analysisResults: [],
  recommendations: [],
  suggestions: [],
  isLoading: false,
  error: null,

  fetchStatus: async () => {
    try {
      const response = await api.get<HashtagApiStatus>('/hashtags/status');
      set({ status: response });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des Status';
      set({ error: message });
    }
  },

  fetchLimits: async () => {
    try {
      const response = await api.get<{ data: HashtagLimitStatus }>('/hashtags/limits');
      set({ limitStatus: response.data });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Limits';
      set({ error: message });
    }
  },

  getSuggestions: async (query: string) => {
    try {
      const response = await api.get<{ data: HashtagSuggestion[]; source: string; apiConfigured: boolean }>(
        `/hashtags/suggest?q=${encodeURIComponent(query)}&limit=10`
      );
      set({ suggestions: response.data });
      return response.data;
    } catch {
      // Silently fail for suggestions - not critical
      return [];
    }
  },

  searchHashtag: async (hashtag: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: { id: string; name: string }; limitStatus: HashtagLimitStatus }>(
        '/hashtags/search',
        { hashtag }
      );
      set({ limitStatus: response.limitStatus, isLoading: false });
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler bei der Hashtag-Suche';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  getHashtagMetrics: async (hashtag: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: HashtagMetrics; limitStatus: HashtagLimitStatus }>(
        '/hashtags/metrics',
        { hashtag }
      );
      set({
        analysisResults: [response.data, ...get().analysisResults.filter(r => r.hashtag !== response.data.hashtag)],
        limitStatus: response.limitStatus,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Metriken';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  analyzeHashtags: async (hashtags: string[]) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: HashtagAnalysisResult }>('/hashtags/analyze', { hashtags });
      set({
        analysisResults: response.data.hashtags,
        recommendations: response.data.recommendations,
        limitStatus: {
          searchesThisWeek: 30 - response.data.limitStatus.remaining,
          remaining: response.data.limitStatus.remaining,
          resetsIn: '7 Tage',
          hashtags: [],
        },
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler bei der Hashtag-Analyse';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  clearResults: () => {
    set({ analysisResults: [], recommendations: [] });
  },

  clearError: () => {
    set({ error: null });
  },
}));
